import {
  AppConfigLike,
  IndicatorsCfg,
  WidgetAvailableCoverageConfigLike,
  WidgetSizeConfigLike,
} from '../interfaces/app-config.interface';

export class AppConfigService {
  private apiUrl: string;
  private services: { [key: string]: string };
  private servicesKeys: string[];

  public readonly serverUrl: string;
  public readonly portalUrl: string;
  public readonly portalAppId: string;

  public readonly helpLink: string;
  public readonly cookiesLink: string;
  public readonly widgetHelpSize: WidgetSizeConfigLike;
  public readonly widgetAvailableCoverage?: WidgetAvailableCoverageConfigLike;
  public readonly indicatorsLinks: IndicatorsCfg[];
  public readonly siiaLink: string;

  public readonly exportLimit: number;

  public readonly loginRequired: boolean;

  public readonly loginPageMode: boolean;

  public readonly externalMode: boolean;
  public readonly internalMode: boolean;
  public readonly siiaMode: boolean;

  get appName() {
    return this.config.appName;
  }

  get mapId() {
    return this.config.mapId;
  }

  get googleAnalyticsId() {
    return this.config.googleAnalyticsId;
  }

  get coverageQuotaConfig() {
    return this.config.coverageQuota;
  }

  get propertyQuotaConfig() {
    return this.config.propertyQuota;
  }

  get searchTokenParam() {
    return this.config.searchTokenParam;
  }

  get toc() {
    return this.config.toc;
  }

  constructor(private config: AppConfigLike) {
    this.apiUrl = config.apiUrl;
    this.services = config.services;
    this.servicesKeys = !this.services ? null : Object.keys(this.services);

    this.cookiesLink = this.normalizeUrl(config.cookiesLink);
    this.helpLink = this.normalizeUrl(config.helpLink);
    this.widgetHelpSize = config.widgetHelpSize;
    this.widgetAvailableCoverage = config.widgetAvailableCoverage;
    this.siiaLink = this.normalizeUrl(config.siiaLink);

    this.indicatorsLinks = (
      Array.isArray(config.indicatorsLinks)
        ? config.indicatorsLinks
        : [config.indicatorsLinks]
    )
      ?.map((x) => {
        if (!x) {
          return null;
        }

        let label: string;
        let link: string;
        if (typeof x === 'string') {
          link = x;
        } else {
          label = x.label;
          link = this.normalizeUrl(x.link);
        }

        return {
          label,
          link,
        };
      })
      ?.filter((x) => !!x);

    this.exportLimit = config.exportLimit ?? 0;

    this.loginRequired = !!config.loginRequired;

    this.loginPageMode = config.loginMode === 'login-page';

    this.externalMode = !this.loginRequired;
    this.internalMode = this.loginRequired && this.loginPageMode;
    this.siiaMode = this.loginRequired && config.loginMode === 'query-token';

    this.portalAppId = config.portalAppId;
    this.portalUrl = this.normalizeUrl('~portalUrl');
    this.serverUrl = this.normalizeUrl('~baseArcGisServer');
  }

  normalizeUrl(url: string): string {
    if (typeof url !== 'string') {
      return null;
    }

    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = this.parseServiceIfNeeded(url);
    }

    url = this.normalizeUrlInternal(url);
    return url;
  }

  isApiUrl(url: string) {
    return url?.startsWith(this.apiUrl) ?? false;
  }

  private parseServiceIfNeeded(url: string) {
    const shouldParseApi = url.startsWith('~');
    if (shouldParseApi) {
      const recursiveFnAux = () => {
        let key: string = '';
        this.servicesKeys?.forEach((x: any) => {
          if (
            url.startsWith('~' + x) &&
            (!key || (key && key.length < x.length))
          ) {
            key = x;
          }
        });
        if (key) {
          url = url.replace('~' + key, this.services[key]);
        } else {
          url = url.replace('~', '');
        }

        if (url.startsWith('~')) {
          recursiveFnAux();
        }
      };

      recursiveFnAux();
    }

    if (
      shouldParseApi &&
      !url.startsWith('http://') &&
      !url.startsWith('https://')
    ) {
      url = this.apiUrl + '/' + url;
    }

    return url;
  }

  private normalizeUrlInternal(url: string) {
    let prefix: string;
    if (url.startsWith('http://')) {
      prefix = 'http://';
    } else if (url.startsWith('https://')) {
      prefix = 'https://';
    } else {
      prefix = '';
    }
    return (
      prefix +
      url
        .substr(prefix.length)
        .replace(/(\/\/)/g, '/')
        .replace(/(\/\/\/)/g, '/')
        .replace(/(\/\/)/g, '/')
        .replace(/(\/\/\/)/g, '/')
    );
  }
}
